<script setup lang="ts">
import { isInstagramBrowserAgent } from '@/utils';
import { useWebsiteJsonLd } from '~/composables/jsonLd/useWebsiteJsonLd';
import { useAuthCrossTabSync } from '~/composables/useAuthCrossTabSync';
import { nuxtRegionService } from '~~/adapters/services/nuxt/nuxtRegionService';
import { appName, siteName } from '~~/app/constants';
import { REGION_SERVICE } from '~~/utils/symbols';
import { ConfigProvider } from 'reka-ui';

import { CookiesWarning } from './components/CookiesBanner';
import { LoginModal } from './components/LoginModal';
import Toaster from './components/ui/toast/Toaster.vue';
import { useEvents } from './composables/events/useEvents';
import { useCanonical } from './composables/useCanonical';

provide(REGION_SERVICE, nuxtRegionService);

// Initialize cross-tab auth sync globally so all tabs listen for auth changes
useAuthCrossTabSync();

const { loggedIn: isUserLoggedIn } = useUserSession();
const router = useRouter();

watchEffect(async () => {
	const isProtectedRoute = router.currentRoute.value.meta.middleware === 'auth';

	if (isProtectedRoute && !isUserLoggedIn.value) await router.push('/');
});

useWebsiteJsonLd();
const { canonicalUrl } = useCanonical();

useHead({
	title: appName,
	htmlAttrs: { lang: 'pt-BR' },
	link: () => [
		// Only add canonical link if it's different from current URL
		...(canonicalUrl.value
			? [
					{
						rel: 'canonical',
						href: canonicalUrl.value
					}
				]
			: []),
		// Critical: Google Maps API
		{
			rel: 'preconnect',
			href: 'https://maps.googleapis.com'
		}
	]
});
useSeoMeta({ ogSiteName: siteName });

const { cookiesAccepted } = useAllowCookies();

const { trackCookiesAccepted } = useEvents();
const cookiesAcceptedCleanup =
	useEventBus('cookies-accepted').on(trackCookiesAccepted);

const isNotTermsPage = computed(
	() => !router.currentRoute.value.path.startsWith('/termos')
);

const shouldShowBanner = ref(false);
const isInstagramBrowser = ref(false);

const hotjarConfig = useRuntimeConfig().public.hotjar as {
	hotjarId: number;
	scriptVersion: number;
	debug: boolean;
};

let hotjarInitialized = false;

function initHotjarWhenIdle() {
	if (hotjarInitialized || !hotjarConfig.hotjarId) return;
	hotjarInitialized = true;

	const doInit = async () => {
		const { default: Hotjar } = await import('@hotjar/browser');
		Hotjar.init(hotjarConfig.hotjarId, hotjarConfig.scriptVersion, {
			debug: hotjarConfig.debug
		});
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(() => doInit(), { timeout: 5000 });
	} else {
		setTimeout(() => doInit(), 3000);
	}
}

const isCookiesNotAccepted = computed(
	() => !cookiesAccepted.value || cookiesAccepted.value === 'not accepted'
);

onMounted(() => {
	if (cookiesAccepted.value === 'accepted') {
		initHotjarWhenIdle();
	}

	isInstagramBrowser.value = isInstagramBrowserAgent();

	nextTick(
		() =>
			(shouldShowBanner.value =
				isNotTermsPage.value && isCookiesNotAccepted.value)
	);
});

watch(cookiesAccepted, () => {
	shouldShowBanner.value = isNotTermsPage.value && isCookiesNotAccepted.value;

	if (cookiesAccepted.value === 'accepted') {
		initHotjarWhenIdle();
	}

	if (isInstagramBrowser.value && cookiesAccepted.value === 'accepted') {
		shouldShowBanner.value = false;
	}
});

onUnmounted(() => {
	cookiesAcceptedCleanup();
});

const useIdFunction = () => useId();
</script>

<template>
	<ConfigProvider :use-id="useIdFunction">
		<NuxtLayout>
			<Toaster />
			<LoginModal />
			<NuxtPage />
			<Transition
				enter-active-class="transition-[transform,opacity] duration-300 ease-out"
				enter-from-class="translate-y-full opacity-0"
				enter-to-class="translate-y-0 opacity-100"
				leave-active-class="transition-[transform,opacity] duration-300 ease-in"
				leave-from-class="translate-y-0 opacity-100"
				leave-to-class="translate-y-full opacity-0"
			>
				<div
					v-if="shouldShowBanner"
					class="pointer-events-auto fixed inset-x-0 bottom-0 z-50 w-full"
				>
					<CookiesWarning />
				</div>
			</Transition>
		</NuxtLayout>
	</ConfigProvider>
</template>
